import React from 'react';

const AboutPage: React.FC = () => {
  // Add SEO meta tags
  React.useEffect(() => {
    document.title = "About ScanOnce 2.0 - Universal Product Authentication Platform";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about ScanOnce 2.0, the universal product authentication platform founded by Mercy Karuoya. Protecting brands across all industries with enterprise-grade verification technology.');
    }
  }, []);

  const stats = [
    { number: '99.9%', label: 'Accuracy Rate', icon: '🎯' },
    { number: '<50ms', label: 'Verification Speed', icon: '⚡' },
    { number: '24/7', label: 'Support Available', icon: '🛡️' },
    { number: '100+', label: 'Industries Supported', icon: '🌍' }
  ];

  const features = [
    {
      title: 'Universal Platform',
      description: 'Works with any product across all industries - alcohol, food, cosmetics, electronics, and more.',
      icon: '🌐',
      color: '#3b82f6'
    },
    {
      title: 'Cryptographic Security',
      description: 'Military-grade encryption makes counterfeiting mathematically impossible.',
      icon: '🔒',
      color: '#8b5cf6'
    },
    {
      title: 'Real-time Analytics',
      description: 'Monitor scan patterns, detect counterfeits, and track product performance instantly.',
      icon: '📊',
      color: '#06b6d4'
    },
    {
      title: 'Enterprise Integration',
      description: 'Seamless integration with existing production lines and business systems.',
      icon: '⚙️',
      color: '#10b981'
    }
  ];

  const timeline = [
    {
      year: '2025',
      title: 'ScanOnce 1.0 Launch',
      description: 'Revolutionary alcohol authentication system launched in Kenya',
      color: '#ef4444'
    },
    {
      year: '2025',
      title: 'Universal Platform',
      description: 'Expanded to support all product categories and industries',
      color: '#f59e0b'
    },
    {
      year: '2025',
      title: 'Enterprise Features',
      description: 'Added manufacturer dashboards, analytics, and billing systems',
      color: '#22c55e'
    },
    {
      year: '2026',
      title: 'Global Expansion',
      description: 'Scaling across East Africa and international markets',
      color: '#3b82f6'
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}></div>

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '40px 20px'
      }}>
        {/* Hero Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 80px',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            padding: '8px 24px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '50px',
            marginBottom: '32px'
          }}>
            <span style={{
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              About ScanOnce 2.0
            </span>
          </div>

          <h1 style={{
            fontSize: '4rem',
            fontWeight: '800',
            color: 'white',
            marginBottom: '32px',
            lineHeight: '1.1',
            letterSpacing: '-0.02em'
          }}>
            Universal Product{' '}
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Authentication
            </span>
          </h1>

          <p style={{
            fontSize: '24px',
            color: '#94a3b8',
            maxWidth: '800px',
            margin: '0 auto 48px',
            lineHeight: '1.6'
          }}>
            Protecting brands and consumers across all industries with enterprise-grade 
            verification technology that makes counterfeiting impossible.
          </p>

          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '32px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {stats.map((stat, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{stat.icon}</div>
                <div style={{
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  {stat.number}
                </div>
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 80px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '60px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '60px',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  marginBottom: '24px',
                  lineHeight: '1.2'
                }}>
                  Our Mission
                </h2>
                <p style={{
                  color: '#64748b',
                  fontSize: '20px',
                  lineHeight: '1.7',
                  marginBottom: '32px'
                }}>
                  To eliminate counterfeiting across all industries through unbreakable 
                  cryptographic technology, protecting both brands and consumers worldwide.
                </p>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Enterprise Ready
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Global Scale
                  </div>
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '20px',
                padding: '40px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <h3 style={{
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Trusted by Industry Leaders
                </h3>
                <p style={{
                  color: '#64748b',
                  fontSize: '16px',
                  lineHeight: '1.5'
                }}>
                  From small businesses to multinational corporations, 
                  ScanOnce 2.0 scales to meet any verification need.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 80px'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            <h2 style={{
              color: 'white',
              fontSize: '3rem',
              fontWeight: '700',
              marginBottom: '24px'
            }}>
              Why Choose ScanOnce 2.0
            </h2>
            <p style={{
              color: '#94a3b8',
              fontSize: '20px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Advanced features designed for enterprise-scale product authentication
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {features.map((feature, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '40px',
                textAlign: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '32px'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  color: '#94a3b8',
                  fontSize: '16px',
                  lineHeight: '1.6'
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 80px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '60px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <h2 style={{
              color: '#1e293b',
              fontSize: '2.5rem',
              fontWeight: '700',
              marginBottom: '48px',
              textAlign: 'center'
            }}>
              Our Journey
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '32px'
            }}>
              {timeline.map((item, index) => (
                <div key={index} style={{
                  position: 'relative',
                  padding: '24px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderRadius: '16px',
                  border: `2px solid ${item.color}20`
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '24px',
                    background: item.color,
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '700'
                  }}>
                    {item.year}
                  </div>
                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    marginTop: '12px'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Founder Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 80px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '60px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '60px',
              alignItems: 'center'
            }}>
              <div style={{
                textAlign: 'center'
              }}>
                <div style={{
                  width: '200px',
                  height: '200px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 20px 40px rgba(139, 92, 246, 0.3)'
                }}>
                  <span style={{
                    color: 'white',
                    fontSize: '4rem',
                    fontWeight: '700'
                  }}>
                    MK
                  </span>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Contact Information
                  </h4>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <a href="mailto:karuoyamercy@gmail.com" style={{
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      📧 karuoyamercy@gmail.com
                    </a>
                    <a href="tel:+254114445264" style={{
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      📱 +254 114 445 264
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  marginBottom: '24px'
                }}>
                  Meet Our Founder
                </h2>
                <h3 style={{
                  color: '#3b82f6',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  Mercy Karuoya
                </h3>
                <p style={{
                  color: '#64748b',
                  fontSize: '18px',
                  lineHeight: '1.7',
                  marginBottom: '24px'
                }}>
                  A visionary Kenyan technology innovator who developed ScanOnce after 
                  witnessing the devastating impact of counterfeit products on communities. 
                  Mercy's mission is to create technology that protects both brands and 
                  consumers worldwide.
                </p>
                <div style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  border: '1px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <p style={{
                    color: '#1e40af',
                    fontSize: '16px',
                    fontStyle: 'italic',
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    "Technology should serve humanity. ScanOnce 2.0 represents our commitment 
                    to creating a world where consumers can trust what they buy, and brands 
                    can protect their reputation."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '60px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center'
          }}>
            <h2 style={{
              color: '#1e293b',
              fontSize: '2.5rem',
              fontWeight: '700',
              marginBottom: '24px'
            }}>
              Ready to Protect Your Brand?
            </h2>
            <p style={{
              color: '#64748b',
              fontSize: '20px',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Join industry leaders using ScanOnce 2.0 to eliminate counterfeiting 
              and protect their customers.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <a 
                href="/verify" 
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  color: 'white',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                Try Verification Demo
              </a>

              <a 
                href="/manufacturer/register" 
                style={{
                  backgroundColor: 'white',
                  color: '#475569',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.color = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#475569';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18"/>
                  <path d="M5 21V7l8-4v18"/>
                  <path d="M19 21V11l-6-4"/>
                </svg>
                Register as Manufacturer
              </a>

              <a 
                href="mailto:karuoyamercy@gmail.com" 
                style={{
                  backgroundColor: 'transparent',
                  color: '#64748b',
                  padding: '16px 32px',
                  textDecoration: 'none',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#22c55e';
                  e.currentTarget.style.color = '#22c55e';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#64748b';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;

